export interface LocalReportingPeriodDates {
  startDate: string;
  stopDate: string;
}

function formatDate(date: Date): string {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0')
  ].join('-');
}

function startOfSundayWeek(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() - date.getDay());
}

function normalizePeriodKey(key: string): string {
  const normalized = key.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  const aliases: Record<string, string> = {
    THIS_WEEK: 'CURRENT_WEEK',
    THIS_MONTH: 'CURRENT_MONTH',
    THIS_YEAR: 'CURRENT_YEAR',
    LAST_WEEK: 'PAST_WEEK',
    LAST_30_DAYS: 'PAST_30_DAYS',
    PREVIOUS_30_DAYS: 'PAST_30_DAYS',
    LAST_MONTH: 'PREVIOUS_MONTH',
    LAST_YEAR: 'PREVIOUS_YEAR'
  };
  return aliases[normalized] ?? normalized;
}

export function getLocalReportingPeriodDates(key?: string | null): LocalReportingPeriodDates | null {
  const period = normalizePeriodKey(key ?? '');
  if (!period) {
    return null;
  }

  const today = new Date();
  const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const yesterday = new Date(todayDate.getFullYear(), todayDate.getMonth(), todayDate.getDate() - 1);
  let startDate: Date;
  let stopDate: Date;

  switch (period) {
    case 'CUSTOM':
    case 'CURRENT_WEEK':
      startDate = startOfSundayWeek(todayDate);
      stopDate = period === 'CUSTOM'
        ? yesterday
        : new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + 6);
      break;
    case 'PAST_WEEK': {
      const previousWeek = new Date(todayDate.getFullYear(), todayDate.getMonth(), todayDate.getDate() - 7);
      startDate = startOfSundayWeek(previousWeek);
      stopDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + 6);
      break;
    }
    case 'PAST_30_DAYS':
      startDate = new Date(todayDate.getFullYear(), todayDate.getMonth(), todayDate.getDate() - 30);
      stopDate = yesterday;
      break;
    case 'LAST_7_DAYS':
      startDate = new Date(todayDate.getFullYear(), todayDate.getMonth(), todayDate.getDate() - 7);
      stopDate = yesterday;
      break;
    case 'CURRENT_MONTH':
      startDate = new Date(todayDate.getFullYear(), todayDate.getMonth(), 1);
      stopDate = new Date(todayDate.getFullYear(), todayDate.getMonth() + 1, 0);
      break;
    case 'PREVIOUS_MONTH':
      startDate = new Date(todayDate.getFullYear(), todayDate.getMonth() - 1, 1);
      stopDate = new Date(todayDate.getFullYear(), todayDate.getMonth(), 0);
      break;
    case 'CURRENT_YEAR':
      startDate = new Date(todayDate.getFullYear(), 0, 1);
      stopDate = yesterday;
      break;
    case 'PREVIOUS_YEAR':
      startDate = new Date(todayDate.getFullYear() - 1, 0, 1);
      stopDate = new Date(todayDate.getFullYear() - 1, 11, 31);
      break;
    default:
      return null;
  }

  return { startDate: formatDate(startDate), stopDate: formatDate(stopDate) };
}
